import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { HELP_CENTRE_ITEM, HELP_SUPPORT_ITEM, NAV_SECTIONS, type NavItem, type NavSection } from './navConfig'

export const APP_TITLE = 'ComfortHub'

/** The menu, plus Help & support at the sidebar's foot (it is not in NAV_SECTIONS): /help and /support. */
const TITLE_SECTIONS: NavSection[] = [...NAV_SECTIONS, { label: '', items: [HELP_CENTRE_ITEM, HELP_SUPPORT_ITEM] }]

interface Candidate {
  item: NavItem
  depth: number
}

function flatten(items: NavItem[], depth = 0): Candidate[] {
  return items.flatMap((item) => [{ item, depth }, ...flatten(item.subItems ?? [], depth + 1)])
}

/**
 * The menu label for a path: the menu item whose route is the longest prefix of
 * it, so /events/abc/attendance is "Events". A parent and its first child often
 * share a route (Orders and Board are both /orders/manage); the child wins,
 * because that's the item the menu highlights.
 */
export function menuLabelForPath(pathname: string, sections: NavSection[] = TITLE_SECTIONS): string | null {
  const path = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname
  let best: Candidate | null = null
  for (const candidate of sections.flatMap((section) => flatten(section.items))) {
    const { to } = candidate.item
    if (path !== to && !path.startsWith(`${to}/`)) continue
    const longer = !best || to.length > best.item.to.length
    const deeper = best !== null && to.length === best.item.to.length && candidate.depth > best.depth
    if (longer || deeper) best = candidate
  }
  return best ? best.item.label : null
}

/** "Stock · ComfortHub", or just "ComfortHub" where no menu item matches. */
export function documentTitleForPath(pathname: string, sections: NavSection[] = TITLE_SECTIONS): string {
  const label = menuLabelForPath(pathname, sections)
  return label ? `${label} · ${APP_TITLE}` : APP_TITLE
}

/** Keeps the browser tab's title in step with the menu. Mounted once, in App. */
export default function DocumentTitle() {
  const { pathname } = useLocation()
  useEffect(() => {
    document.title = documentTitleForPath(pathname)
  }, [pathname])
  return null
}
